import type {
  CurrencyRef,
  DealCommitmentRef,
  DealMeasurementBindingRef,
  DealRef,
  DealThesisOutcomeRef,
  FiscalCalendarRef,
  GovernedDateRef,
  GovernedEntityRef,
  GovernedMetricRef,
  GovernedSeatRef,
  PolicyRef,
  PromiseSetRef,
  ReceiptRef,
  TenantRef,
} from './ma-deal-refs';
import type { SynergyCertaintyClass } from './ma-synergy-commitment';
import type { DecisionAgentSourceClass } from './contracts/sourceClasses';

export type PromiseBand = 'conservative' | 'base' | 'stretch';
export type ValueBasis = 'run_rate' | 'in_period' | 'cumulative' | 'one_time';
export type PromiseKind =
  | 'investment_committee'
  | 'board_approved'
  | 'modeled_underwriting_overlay'
  | 'publicly_communicated'
  | 'close_date_integration';
export type PromiseSetGovernanceState = 'draft' | 'approved' | 'superseded';
export type CommitmentType =
  | 'synergy_benefit'
  | 'dis_synergy_or_leakage'
  | 'cost_to_achieve'
  | 'standalone_performance'
  | 'retention_or_asset_protection'
  | 'strategic_outcome';
export type BenefitDisposition = 'benefit' | 'dis_synergy';
export type CommitmentRecurrence = 'recurring' | 'one_time';
export type MeasurementBindingStatus = 'bound' | 'not_measurable';

export type PromiseBandSemantics = Readonly<{
  coherentPortfolio: boolean;
  bands: readonly PromiseBand[];
}>;

export type BandTarget = Readonly<{
  value: number;
  valueBasis: ValueBasis;
}>;

export type DealCommitmentAllocationTimingV1 = Readonly<{
  plannedStartDateRef: GovernedDateRef | null;
  targetDateRef: GovernedDateRef | null;
}>;

export type DealCommitmentAllocationNodeBaseV1 = Readonly<{
  nodeId: string;
  parentNodeId: string | null;
  order: number;
  label: string;
  baselineByBand: Readonly<Record<PromiseBand, BandTarget>>;
  targetByBand: Readonly<Record<PromiseBand, BandTarget>>;
  retainedByBand: Readonly<Record<PromiseBand, BandTarget>>;
  costToAchieve: BandTarget;
  disposition: 'retire' | 'retain';
  protectedSystem: boolean;
  timing: DealCommitmentAllocationTimingV1;
  sourceClass: DecisionAgentSourceClass;
}>;

export type DealCommitmentAllocationSubtotalNodeV1 =
  DealCommitmentAllocationNodeBaseV1 &
  Readonly<{
    kind: 'subtotal';
    inventoryRef: null;
  }>;

export type DealCommitmentAllocationLeafNodeV1 =
  DealCommitmentAllocationNodeBaseV1 &
  Readonly<{
    kind: 'leaf';
    inventoryRef: string;
  }>;

export type DealCommitmentAllocationNodeV1 =
  | DealCommitmentAllocationSubtotalNodeV1
  | DealCommitmentAllocationLeafNodeV1;

/** A versioned, scenario-specific partition of one admitted commitment. */
export type DealCommitmentAllocationHeaderV1 = Readonly<{
  schemaVersion: 'deal_commitment_allocation_breakdown_v1';
  parentCommitmentRef: DealCommitmentRef;
  scenarioRef: string;
  version: number;
  contentDigest: string;
  valueBasis: ValueBasis;
  sourceClass: DecisionAgentSourceClass;
  nodes: readonly DealCommitmentAllocationNodeV1[];
}>;

export type DealCommitmentAllocationHeaderSelectionV1 = Readonly<{
  parentCommitmentRef: DealCommitmentRef;
  scenarioRef: string;
  version: number;
}>;

/** A reader must name one header identity; aggregation across scenarios is prohibited. */
export function selectDealCommitmentAllocationHeader(
  headers: readonly DealCommitmentAllocationHeaderV1[],
  selection: DealCommitmentAllocationHeaderSelectionV1,
): DealCommitmentAllocationHeaderV1 | null {
  const matches = headers.filter((header) =>
    header.parentCommitmentRef === selection.parentCommitmentRef &&
    header.scenarioRef === selection.scenarioRef &&
    header.version === selection.version,
  );
  return matches.length === 1 ? matches[0] ?? null : null;
}
export type DealCaseV1 = Readonly<{
  schemaVersion: 'deal_case_v1';
  dealRef: DealRef;
  tenantRef: TenantRef;
  displayName: string;
  acquirerEntityRef: GovernedEntityRef;
  targetEntityRef: GovernedEntityRef;
  includedEntityRefs: readonly GovernedEntityRef[];
  excludedEntityRefs: readonly GovernedEntityRef[];
  announcementDateRef?: GovernedDateRef;
  signingDateRef?: GovernedDateRef;
  closeDateRef?: GovernedDateRef;
  integrationStartDateRef?: GovernedDateRef;
  reportingCurrencyRef: CurrencyRef;
  fiscalCalendarRef: FiscalCalendarRef;
  promiseSetRefs: readonly PromiseSetRef[];
  thesisOutcomeRefs: readonly DealThesisOutcomeRef[];
  activeAssessmentPolicyRef?: PolicyRef;
}>;

export type DealCommitmentV1 = Readonly<{
  schemaVersion: 'deal_commitment_v1';
  commitmentRef: DealCommitmentRef;
  displayLabel: string;
  dealRef: DealRef;
  promiseSetRef: PromiseSetRef;
  supersedesCommitmentRef?: DealCommitmentRef;
  commitmentType: CommitmentType;
  benefitDisposition: BenefitDisposition;
  recurrence: CommitmentRecurrence;
  certaintyClass: SynergyCertaintyClass;
  baselineRef: ReceiptRef;
  targetByBand: Readonly<Record<PromiseBand, BandTarget>>;
  targetDateRef: GovernedDateRef;
  realizationCurveRef: ReceiptRef;
  costToAchieve: BandTarget;
  accountableSeatRef: GovernedSeatRef;
  thesisOutcomeRefs: readonly DealThesisOutcomeRef[];
  measurementBindingRef: DealMeasurementBindingRef | null;
  bandSemantics: PromiseBandSemantics;
}>;

export type DealMeasurementBindingV1 = Readonly<{
  schemaVersion: 'deal_measurement_binding_v1';
  measurementBindingRef: DealMeasurementBindingRef;
  commitmentRef: DealCommitmentRef;
  moduleId: string;
  operationId: string;
  metricKey: GovernedMetricRef;
  formulaVersion: string;
  scopeRef: GovernedEntityRef;
  unit: string;
  periodRef: PolicyRef;
  baselineOrCounterfactualPolicyRef: PolicyRef;
  recognitionPolicyRef: PolicyRef;
  requiredEvidenceKeys: readonly string[];
  freshnessPolicyRef: PolicyRef;
  attributionPolicyRef: PolicyRef;
  requiresFinanceReconciliation: boolean;
}>;

export type PromiseSetV1 = Readonly<{
  schemaVersion: 'deal_promise_set_v1';
  promiseSetId: PromiseSetRef;
  dealRef: DealRef;
  version: number;
  contentDigest: string;
  approvedAt: string | null;
  approvedBy: GovernedSeatRef | null;
  sourceDocumentRefs: readonly ReceiptRef[];
  bandSemantics: PromiseBandSemantics;
  reportingCurrencyRef: CurrencyRef;
  fiscalCalendarRef: FiscalCalendarRef;
  valueBasis: ValueBasis;
  supersedesPromiseSetRef?: PromiseSetRef;
  governanceState: PromiseSetGovernanceState;
  promiseKind: PromiseKind;
  commitmentRefs: readonly DealCommitmentRef[];
  commitments: readonly DealCommitmentV1[];
}>;

export type PromiseSetDraft = Readonly<
  Omit<PromiseSetV1, 'contentDigest' | 'governanceState'> & {
    contentDigest?: string;
    governanceState: 'draft';
  }
>;

export type PromiseSetRevision = Readonly<{
  promiseSetId: PromiseSetRef;
  approvedAt?: string | null;
  approvedBy?: GovernedSeatRef | null;
  sourceDocumentRefs?: readonly ReceiptRef[];
  bandSemantics?: PromiseBandSemantics;
  reportingCurrencyRef?: CurrencyRef;
  fiscalCalendarRef?: FiscalCalendarRef;
  valueBasis?: ValueBasis;
  promiseKind?: PromiseKind;
  commitmentRefs?: readonly DealCommitmentRef[];
  commitments?: readonly DealCommitmentV1[];
  contentDigest?: string;
}>;

export type CoherentBandTotal = Readonly<{
  band: PromiseBand;
  value: number;
  valueBasis: ValueBasis;
}>;
